import { useEffect, useRef, useState } from "react";
import WhatsAppWidget from "react-whatsapp-chat-widget";
import "react-whatsapp-chat-widget/index.css";
import { useTenantMetadataStore } from "../../store/useTenantMetadataStore";
import { useTenantImages } from "../../tenantMatrix/TenantImages";
import { ChatbotClientMeRepository } from "../../infrastructure/ChatbotSlice/ChatbotClientMeRepository";
import "./ChatbotWhatsappBubble.css";
import { SiProbot } from "react-icons/si";
import { AiOutlineClose } from "react-icons/ai";
import { useSecurityStore } from "../../store/useSecurityStore";
import { useAnonStore } from "../../store/useAnonStore";
import { ChatbotAnonRepository } from "../../infrastructure/ChatbotSlice/ChatbotAnonRepository";
import { ChatbotAdminRepository } from "../../infrastructure/ChatbotSlice/ChatbotAdminRepository";
import { ChatbotTransporterRepository } from "../../infrastructure/ChatbotSlice/ChatbotTransporterRepository";
import { ChatbotPackerRepository } from "../../infrastructure/ChatbotSlice/ChatbotPackerRepository";
import globalRoles from "../../globalRoles";

// Simple friendly chatbot SVG icon
const RobotIcon = ({ size = 30, color = "white" }: { size?: number; color?: string }) => (
  <div className="wb-robot" aria-label="Abrir chatbot">
    <SiProbot size={size} color={color} />
  </div>
);

type ChatMessage = { id: string; role: "user" | "bot"; text: string };

const ChatbotWhatsappBubble = () => {
  const { metadata } = useTenantMetadataStore();
  const { logoExpressPngImg } = useTenantImages();

  const [isOperator, setIsOperator] = useState(false); // false = chatbot, true = WhatsApp
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const clientRepoRef = useRef<ChatbotClientMeRepository>();
  const anonRepoRef = useRef<ChatbotAnonRepository>();
  const adminRepoRef = useRef<ChatbotAdminRepository>();
  const transporterRepoRef = useRef<ChatbotTransporterRepository>();
  const packerRepoRef = useRef<ChatbotPackerRepository>();
  const { isLoggedIn, hasRole } = useSecurityStore();
  const { getAnonSessionId } = useAnonStore();

  useEffect(() => {
    clientRepoRef.current = new ChatbotClientMeRepository();
    anonRepoRef.current = new ChatbotAnonRepository();
    adminRepoRef.current = new ChatbotAdminRepository();
    transporterRepoRef.current = new ChatbotTransporterRepository();
    packerRepoRef.current = new ChatbotPackerRepository();
  }, []);

  // Seed initial friendly bot greeting when opening chatbot for the first time
  useEffect(() => {
    if (isOpen && !isOperator && messages.length === 0) {
      setMessages([
        {
          id: "greet",
          role: "bot",
          text: "Hola! 👋\n\n¿En qué podemos ayudarte?",
        },
      ]);
    }
  }, [isOpen, isOperator, messages.length]);

  const phoneNo = metadata?.phone ?? "";
  const tenantName = metadata?.name ?? "Soporte";

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    const userMsg: ChatMessage = { id: String(Date.now()), role: "user", text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    try {
      const isAdmin = isLoggedIn && hasRole(globalRoles.ADMIN);
      const isTransporter = isLoggedIn && hasRole(globalRoles.TRANSPORTER);
      const isPacker = isLoggedIn && hasRole(globalRoles.PACKER);
      const res = isLoggedIn
        ? isAdmin
          ? await adminRepoRef.current!.ask({ message: trimmed })
          : isTransporter
            ? await transporterRepoRef.current!.ask({ message: trimmed })
            : isPacker
              ? await packerRepoRef.current!.ask({ message: trimmed })
              : await clientRepoRef.current!.ask({ message: trimmed })
        : await anonRepoRef.current!.ask({ anonSessionId: getAnonSessionId(), message: trimmed });
      const answer = res?.answer?.trim() || "Perdón, no pude procesar tu consulta ahora mismo.";
      const botMsg: ChatMessage = { id: String(Date.now() + 1), role: "bot", text: answer };
      setMessages((prev) => [...prev, botMsg]);
    } catch {
      const botMsg: ChatMessage = {
        id: String(Date.now() + 2),
        role: "bot",
        text: "Ocurrió un error enviando tu mensaje. Intenta nuevamente en unos segundos.",
      };
      setMessages((prev) => [...prev, botMsg]);
    } finally {
      setSending(false);
    }
  };

  // Small responsive sizes
  const widgetWidth = "300px";
  const widgetWidthMobile = "260px";

  // (removed auto-open on operator to keep bubble state consistent)

  return (
    <>
      {/* Chatbot launcher bubble (hidden when operator mode because WhatsApp widget has its own) */}
      {!isOperator && (
        <button
          type="button"
          className="wb-launcher"
          onClick={() => setIsOpen((v) => !v)}
          aria-expanded={isOpen}
          aria-controls="chatbot-panel"
        >
          <RobotIcon size={25} />
        </button>
      )}

      {/* Chatbot panel */}
      {!isOperator && isOpen && (
        <div id="chatbot-panel" className="wb-panel" role="dialog" aria-label="Chat de soporte">
          <div className="wb-header">
            <div className="wb-header-left">
              {logoExpressPngImg?.url ? (
                <img src={logoExpressPngImg.url} alt={tenantName} className="wb-logo" />
              ) : (
                <div className="wb-logo-fallback" />
              )}
              <div className="wb-title-wrap">
                <span className="wb-title">{tenantName}</span>
                <span className="wb-caption">Online</span>
              </div>
            </div>
            <div className="wb-toggle-wrap">
              <span>hablar con un operador</span>
              <div
                role="switch"
                aria-checked={isOperator}
                onClick={() => {
                  setIsOperator(true);
                  setIsOpen(true);
                }}
                className="wb-switch"
              >
                <div className="wb-switch-knob" />
              </div>
            </div>
            <button
              type="button"
              className="wb-close"
              aria-label="Cerrar chat"
              onClick={() => setIsOpen(false)}
            >
              <AiOutlineClose size={18} />
            </button>
          </div>

          <div className="wb-body">
            {messages.map((m) => (
              <div key={m.id} className={m.role === "user" ? "wb-bubble-user" : "wb-bubble-bot"}>
                {m.text}
              </div>
            ))}
            {sending && <div className="wb-bubble-bot">Escribiendo…</div>}
          </div>

          <div className="wb-footer">
            <input
              type="text"
              className="wb-input"
              placeholder="Escribe tu mensaje"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={sending}
            />
            <button type="button" className="wb-send-btn" onClick={handleSend} disabled={sending}>
              Enviar
            </button>
          </div>
        </div>
      )}

      {/* Operator/WhatsApp widget */}
      {isOperator && (
        <>
          {/* Quick toggle to go back to chatbot */}
          <button
            type="button"
            className="wb-back-toggle"
            onClick={() => {
              setIsOperator(false);
              setIsOpen(true);
            }}
          >
            ← usar chatbot
          </button>
          <WhatsAppWidget
            phoneNo={phoneNo}
            position="right"
            widgetWidth={widgetWidth}
            widgetWidthMobile={widgetWidthMobile}
            autoOpen={isOpen}
            iconSize="45"
            iconColor="white"
            iconBgColor="var(--primary-color)"
            headerIcon={logoExpressPngImg?.url}
            headerIconColor="var(--primary-color)"
            headerTxtColor="var(--font-color-primary, white)"
            headerBgColor="var(--primary-color)"
            headerTitle={tenantName}
            headerCaption="Online"
            bodyBgColor="var(--surface-color, #ece5dd)"
            chatPersonName="Soporte"
            chatMessage={
              <>
                Hola! 👋 <br />
                <br /> En qué podemos ayudarte?
              </>
            }
            footerBgColor="var(--modal-background-color, #ece5dd)"
            btnBgColor="var(--secondary-color, #25d366)"
            btnTxt="Iniciar Chat"
            btnTxtColor="var(--font-color-secondary, white)"
          />
        </>
      )}
    </>
  );
};

export default ChatbotWhatsappBubble;
